import { useState, useEffect, useMemo, useCallback } from "react"
import axios from "axios";
import { toast } from "react-toastify";
import { Trash2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { normalizeOrdersResponse } from "../utils/helpers.js";
import "./MetalOrderFormPage.css";


const blobBase = process.env.REACT_APP_BLOB_SAS_BASE;
const blobToken = process.env.REACT_APP_BLOB_SAS_TOKEN;
const emailOrders = process.env.REACT_APP_ORDER;
const deleteOrders = process.env.REACT_APP_DELETE_ORDER;
const inventoryRoute = process.env.REACT_APP_INVENTORY;
const vendors = process.env.REACT_APP_VENDORS;


const withCacheBust = (url) => {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}cd=${Date.now()}`;
};

// this needs to be in EST
const EST_TZ = "America/New_York";

/** Get YYYY-MM-DD for a given Date, rendered in EST/EDT */
export function toYMD_EST(d = new Date()) {
  const dt = d instanceof Date ? d : new Date(d);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EST_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(dt);
  const get = (t) => parts.find(p => p.type === t)?.value;
  // en-US gives parts we can reassemble as YYYY-MM-DD
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function sameYMD(leftIsoOrYmd, rightYmd) {
  if (!leftIsoOrYmd || !rightYmd) return false;
  const left = leftIsoOrYmd.includes("T")
    ? leftIsoOrYmd.slice(0, 10)     // ISO -> YYYY-MM-DD
    : leftIsoOrYmd;                 // already YYYY-MM-DD
  return left === rightYmd;
}

export function toMDY(ymd) {
  if (!ymd || !ymd.includes("-")) return ymd;
  const [y, m, d] = ymd.split("-");
  return `${m}/${d}/${y}`;
}

function groupByVendor(rows) {
  const map = new Map();
  for (const r of rows) {
    const v = String(r.vendor || "UNKNOWN").toUpperCase();
    if (!map.has(v)) map.set(v, []);
    map.get(v).push(r);
  }
  // sort vendors & items (optional)
  return Array.from(map, ([vendor, items]) => ({
    vendor,
    items: items.sort((a, b) =>
      (a.supplier || "").localeCompare(b.supplier || "") ||
      (a.part || "").localeCompare(b.part || "")
    ),
  })).sort((a, b) => a.vendor.localeCompare(b.vendor));
}




export default function MetalOrderFormPage({ branchName, dept, user }) {

  const [allItems, setAllItems] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [inventoryError, setInventoryError] = useState("");
  const [rows, setRows] = useState([]);
  // const [todaysOrders, setTodaysOrders] = useState([]);
  const [error, setError] = useState("");
  const [posting, setPosting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const [orders, setOrders] = useState([]);

  const vendorOptions = useMemo(() => parseVendors(vendors), [vendors]);
  const defaultVendor = vendorOptions[0]?.value;


  function parseVendors(vendors) {
    if (!vendors) return [];

    try {
      const array = JSON.parse(vendors);

      if (Array.isArray(array)) {
        return array.map(x => (typeof x === "string" ? { value: x, label: x } : x)).filter(x => x?.value);
      }
    } catch (error) {
      console.log("Vendors is not JSON, parsing as string list.");
    }

    return vendors.split(",").map(vendor => {
      const [value, label] = vendor.split(":").map(t => t.trim());
      return { value, label: label || value };
    });
  }

  // function to validate all fields are filled, and vendor CANNOT equal the default "Select Vendor"
  function validateOrderRows() {

    const missingValues = [];

    // push row number and value missing for row number
    rows.forEach((r, i) => {
      if (!r.partText) missingValues.push(`Row ${i + 1} Part`);
      if (!r.descText) missingValues.push(`Row ${i + 1} Description`);
      if (!r.qty || r.qty <= 0) missingValues.push(`Row ${i + 1} Quantity`);
      if (!r.vendor || r.vendor === defaultVendor) missingValues.push(`Row ${i + 1} Vendor`);
    });
    return {
      valid: missingValues.length === 0,
      missingValues,
    };
  }


  const maxSuggestions = 5;

  async function processOrderRequest() {
    // order needs to grab supplier, part, description, quatity and vendor to submit to backed at api/emailorders

    try{
      const validation = validateOrderRows();
      if (!validation.valid) {
        toast.error(`Missing ${validation.missingValues.join(", ")}`, { autoClose: 8000 });
        // stop submission but leave the data in the form and DO NOT REFRESH page
        return;
      }
    } catch (e) {
      console.error("Error processing order request:", e);
      toast.error("An unexpected error occurred while processing the order request.");
      return;
    }

    const payload = {
      submittedBy: user,
      rows: rows.map(r => ({
      supplier: "MTL",
      part: r.partText,
      description: r.descText,
      quantity: r.qty,
      vendor: r.vendor,
    })).filter(r => r.part && r.description && r.quantity > 0 && r.vendor),
    };

    if (payload.rows.length === 0) {
      toast.error("No valid order rows to submit.");
      return;
    }

    try {
      const response = await axios.post(emailOrders, { orders: payload }, { validateStatus: () => true });

      if (response.status === 200) {
        toast.success("Order request submitted successfully.");
        setRows([newRow()]); // reset form
      } else {
        const msg = response.data?.message || `Error ${response.status}: ${response.statusText}`;
        toast.error(`Failed to submit order request. ${msg}`);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Request failed.';
      toast.error(`Failed to submit order request. ${msg}`);
    }
  }

  async function handleOrderRequest() {
    if (posting) return;
    console.log(`Submitting order requests to order list...`);
    try {
      setPosting(true);
      const processing = await processOrderRequest();
      if (processing === false) {
        setPosting(false);
      }      
    } finally {
      setPosting(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();

    setInventoryLoading(true);
    setError("");

    axios.post(inventoryRoute, {}, { signal: controller.signal, validateStatus: () => true })
      .then(({ data }) => {

        const arr =
          Array.isArray(data) ? data :
            Array.isArray(data?.Response?.InventoryItems) ? data.Response.InventoryItems :
              Array.isArray(data?.items) ? data.items : [];

        setAllItems(arr);
      })
      .catch(error => {
        if (axios.isCancel?.(error) || error.name === "CanceledError") return;
        setError(error.response?.data?.message || error.message || 'Request failed.');
      })
      .finally(() => setInventoryLoading(false))

    return () => controller.abort();
  }, [inventoryRoute]);

  const normalize = (s) =>
    (s ?? "")
      .toString()
      .normalize("NFKD")
      .toLowerCase()
      .replace(/[^\w\s.-]/g, "");


  const newRow = () => ({
    id: crypto.randomUUID?.() || String(Date.now() + Math.random()),
    vendor: "",
    qty: 0,
    quantity: undefined,
    partText: "",
    descText: "",
    selectedItemId: null,
    showPartSug: false,
    showDescSug: false,
  });

  const updateRow = (index, patch) =>
    setRows((row) =>
      row.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );

  const chooseItem = (index, item) => {
    updateRow(index, {
      selectedItemId: item.id,
      partText: item.part || "",
      descText: item.description || "",
      showPartSug: false,
      showDescSug: false,
    });
  };

  const onAddRow = () => setRows((row) => [...row, newRow()]);

  const onRemoveRow = (index) => setRows((row) => row.filter((_, i) => i !== index));

  const buildSuggesters = (items) => {
    const index = items.map(item => ({
      id: item.id || item.InventoryItemId,
      part: item.part || item.Part || "",
      description: item.description || item.Description || "",
      supplier: item.supplier || item.Supplier || "",
      _p: normalize(item.part || item.Part || ""),
      _d: normalize(item.description || item.Description || ""),
    }));

    return {
      part: (text) => {
        const t = normalize(text);
        if (!t) return [];
        
        // Get all matches
        const matches = index.filter(x => x._p.includes(t));
        
        // Sort to prioritize exact matches first, then others
        const sorted = matches.sort((a, b) => {
          const aExact = a._p === t ? 0 : 1;
          const bExact = b._p === t ? 0 : 1;
          if (aExact !== bExact) return aExact - bExact;
          return a.part.localeCompare(b.part);
        });
        
        return sorted.slice(0, maxSuggestions);
      },
      description: (text) => {
        const t = normalize(text);
        if (!t) return [];
        return index.filter(x => x._d.includes(t)).slice(0, maxSuggestions);
      }
    };
  };

  const { part, description } = useMemo(() => buildSuggesters(allItems), [allItems]);

  // Ensure at least one row exists on mount
  useEffect(() => {
    if (rows.length === 0) {
      setRows([newRow()]);
    }
  }, []);

  //need a useeffect to grab the quantity  of an order if part number, description and vendor match an already placed order in the blob fro today
  

  useEffect(() => {
    if (rows.length === 0) return;
  
    // should only fire when part/supplier/description/vendor are all filled out for the row

    const hasReadyMissingQty = rows.some(r => {
      const supplier = "MTL";
      const part = (r.part || r.partText || "").trim();
      const desc = (r.description || r.descText || "").trim();
      const vendor = (r.vendor || "").trim();
      return supplier && part && desc && vendor && (r.quantity === undefined);
    });
  if (!hasReadyMissingQty) return;

  const fetchQuantities = async () => {
    const todaysGrouped = await getOrderQt(); // returns { TOWNCO: [ ... ], PHOENIX: [ ... ] }

    setRows(currentRows =>
      currentRows.map(r => {
        const supplier = "MTL";
        const part = (r.part || r.partText || "").trim();
        const desc = (r.description || r.descText || "").trim();

        // inline vendor CODE extraction: "Name (CODE)" -> "CODE", else uppercase as-is
        const vraw = String(r.vendor || "").trim();
        const m = vraw.match(/\(([^)]+)\)/);
        const vendorCode = (m ? m[1] : vraw).toUpperCase();

        if (!(supplier && part && desc && vendorCode)) return r;     // not ready

        // look ONLY inside this vendor's array in the grouped blob
        const list = Array.isArray(todaysGrouped[vendorCode]) ? todaysGrouped[vendorCode] : [];
        const hit = list.find(it =>
          String(it.supplier) === supplier &&
          String(it.part) === part &&
          String(it.description) === desc
        );

        if (!hit) return r;

        const newQty = Number(hit.quantity) || 0;
        // avoid useless state churn
        return (r.quantity !== newQty) ? { ...r, quantity: newQty } : r;
      })
    );
  };

  fetchQuantities();
  }, [rows]);

async function getOrderQt() {
    // fetch blob and filter for today's date
    const url = withCacheBust(`${blobBase}metalForm/DAILYORDER.json${blobToken}`);
    try {
      const response = await axios.get(url, { validateStatus: () => true });  
      if (response.status === 200) {
      const fetchedOrders = response.data || {};
      setOrders(fetchedOrders);
      return fetchedOrders;  
      } else {
        console.error(`Error fetching today's orders: ${response.status} ${response.statusText}`);
        return {};
      }
    } catch (error) {
      console.error("Error fetching today's orders:", error);
      return {};
    }
  };

// async function fetchTodaysOrdersGrouped() {
//   const url = withCacheBust(`${blobBase}metalForm/DAILYORDER.json${blobToken}`);
//   try {
//     const response = await axios.get(url, { validateStatus: () => true });
//     if (response.status === 200) {
//       return response.data || {};  // { PHOENIX: [...], TOWNCO: [...] }
//     }
//     console.error(`Error fetching today's orders: ${response.status} ${response.statusText}`);
//     return {};
//   } catch (err) {
//     console.error("Error fetching today's orders:", err);
//     return {};
//   }
// }

async function getTodaysOrders() { 
  const url = withCacheBust(`${blobBase}metalForm/DAILYORDER.json${blobToken}`);
  try {
    const response = await axios.get(url, { validateStatus: () => true });
    if (response.status === 200) {
      const grouped = response.data || {};
      const arr = normalizeOrdersResponse(grouped); // → [{ supplier, part, description, quantity, vendor }, ...]
      setOrders(arr);
    } else if (response.status === 404) {
      setOrders([]);
    } else {
      console.error(`Error fetching today's orders: ${response.status} ${response.statusText}`);
      setOrders([]);
    }
  } catch (err) {
    console.error("Error fetching today's orders:", err);
    setOrders([]);
  } finally {
    setOpen(true);
  }
};


// async function getOrderHistory() {
//     const url = withCacheBust(`${blobBase}metal/ORDERHISTORY.json${blobToken}`);
//     try {
//       const response = await axios.get(url, { validateStatus: () => true });
//       if (response.status === 200) {
//         const fetchedHistory = response.data || {};
//         setOrderHistory(fetchedHistory);
//         return fetchedHistory;
//       } else {
//         console.error(`Error fetching order history: ${response.status} ${response.statusText}`);
//         return {};
//       }
//     } catch (error) {
//       console.error("Error fetching order history:", error);
//       return {};
//     }
// }

//   async function openOrderHistory() {
//   const url = withCacheBust(`${blobBase}metalForm/ORDERHISTORY.json${blobToken}`);
//   const res = await axios.get(url, { validateStatus: () => true });
//   setOrderHistory(res.status === 200 ? (res.data || {}) : {});
//   setHistoryOpen(true);
// }

  const controlsDisabled = inventoryLoading || !!inventoryError;

  return (
    <div className="deposit-div">
      <h1 className="log-title">
        Metal Order Form
      </h1>
      <p style={{ fontFamily: "'Gantari', sans-serif", fontSize: "1.3rem", fontWeight: "400" }}>Use this form to build a metal order.</p>
      <p style={{ fontFamily: "'Gantari', sans-serif", fontSize: "1.3rem", fontWeight: "400" }}>This form can be used throughout the day.</p>
{/* <div style={{ margin: "1rem", textAlign: "right" }}>
  <section>
    <button
      type="button"
      onClick={openOrderHistory}
      className="order-history-btn"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          gap: 4
        }}
      >
        <span>Order</span>
        <ClipboardList size={30} aria-hidden="true" />
        <span>History</span>
      </div>
    </button>
  </section>
</div> */}
      <div className="deposit-bg-div"> 
        <button onClick={() => { getTodaysOrders()}} className="todays-orders-btn">View Today's Orders</button>
                  {inventoryLoading && <p><strong>Loading Inventory...</strong></p>}                
          {error && <p style={{ color: "red" }}><strong><span style={{ color: "red"}}> Error loading inventory: {error}</span></strong></p>}
        <form noValidate autoComplete="off" onSubmit={e => { e.preventDefault(); handleOrderRequest(); }}>
        <div className="deposit-list-div">

          <div className="deposit-list">

            <fieldset className={`fieldset ${controlsDisabled ? 'is-disabled' : ''}`}
              disabled={controlsDisabled}
              aria-busy={inventoryLoading}
              aria-disabled={controlsDisabled}>
              <table className="deposits-table">
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th>Part</th>
                    <th>Description</th>
                    <th>Order Quantity</th>
                    <th>Vendor Select</th>
                    <th>Qty. in List</th>
                  </tr>
                </thead>

                <tbody>
                  <AnimatePresence initial={false}>
                    {rows.map((row, index) => {
                      const partSug = part(row.partText);
                      const descSug = description(row.descText);

                      const closeLater = (key) => setTimeout(() => updateRow(index, { [key]: false }), 150);

                      return (
                        <motion.tr
                          key={row.id}
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4, transition: { duration: 0.2 } }}
                          style={{ position: "relative" }}
                        >
                          {/* Regular cells – add data-label so they get titled on mobile */}
                          <td data-label="Supplier"><strong>MTL</strong></td>

                          <td data-label="Part" style={{ position: "relative" }}>
                            <input
                              placeholder="Part No. Search"
                              type="text"
                              value={row.partText}
                              onChange={(e) =>
                                updateRow(index, { partText: e.target.value, showPartSug: true, selectedItemId: null })
                              }
                              onFocus={() => updateRow(index, { showPartSug: true })}
                              onBlur={() => closeLater("showPartSug")}
                              autoComplete="off"
                            />
                            {row.showPartSug && row.partText && partSug.length > 0 && (
                              <ul className="suggestions">
                                {partSug.map(item => (
                                  <li
                                    key={item.id}
                                    onMouseDown={(e) => { e.preventDefault(); chooseItem(index, item); }}
                                  >
                                    <strong>{item.part}</strong>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>

                          <td data-label="Description" style={{ position: "relative" }}>
                            <input
                              placeholder="Description Search"
                              type="text"
                              value={row.descText}
                              onChange={(e) =>
                                updateRow(index, { descText: e.target.value, showDescSug: true, selectedItemId: null })
                              }
                              onFocus={() => updateRow(index, { showDescSug: true })}
                              onBlur={() => closeLater("showDescSug")}
                              autoComplete="off"
                            />
                            {row.showDescSug && row.descText && descSug.length > 0 && (
                              <ul className="suggestions">
                                {descSug.map(item => (
                                  <li
                                    key={item.id}
                                    onMouseDown={(e) => { e.preventDefault(); chooseItem(index, item); }}
                                  >
                                    <strong>{item.description}</strong>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>

                          <td data-label="Order Quantity">
                            <input
                              placeholder="Quantity"
                              type="number"
                              min={1}
                              step={1}
                              value={row.qty}
                              onChange={(e) =>
                                updateRow(index, { qty: e.target.value === "" ? "" : Math.max(1, Number(e.target.value)) })
                              }
                            />
                          </td>

                          <td data-label="Vendor">
                            <div className="select-wrap">
                              <strong><select
                                className="vendor-select"
                                value={row.vendor}
                                onChange={(e) => updateRow(index, { vendor: e.target.value })}
                              >
                                {vendorOptions.map(v => (
                                  <option key={v.value} value={v.value}>{v.label}</option>
                                ))}
                              </select></strong>
                            </div>
                          </td>

                          <td data-label="Qty. in List"><strong>{Number.isFinite(row.quantity) ? row.quantity : ""}</strong></td>

                          <td className="actions-cell">
                            <button className="remove-btn" type="button" onClick={() => onRemoveRow(index)}>
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}

                  </AnimatePresence>
                </tbody>

              </table>
              <button
                className="add-row-btn"
                type="button"
                onClick={onAddRow}
              >
                + Add Row

              </button>
            </fieldset>

          </div>

        </div>

        <button
          className="process-deposits"
        >
          Submit Order Request
        </button>
        </form>
      </div>
      <TodaysOrdersModal   open={open}
        onClose={() => setOpen(false)}
        orders={orders}/>

      <OrderHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  );
}



function TodaysOrdersModal({ open, onClose, orders = [] }) {
  const [rows, setRows] = useState([]);

  useEffect(() => { setRows(Array.isArray(orders) ? orders : []); }, [orders]);

  // Delete locally + from blob (by vendor/supplier/part/description)
  const deleteRowEverywhere = useCallback(async (row) => {
    // optimistic UI
    setRows(prev => prev.filter(r =>
      !(String(r.vendor).toUpperCase() === String(row.vendor).toUpperCase() &&
        String(r.supplier) === String(row.supplier) &&
        String(r.part) === String(row.part) &&
        String(r.description) === String(row.description))
    ));

    try {
      const res = await axios.post(deleteOrders, {
        vendor: String(row.vendor).toUpperCase(),
        supplier: row.supplier,
        part: row.part,
        description: row.description,
        // dateYmd: todayYmdET(), // include if you switched to per-day files
      }, { validateStatus: () => true });

      if (res.status !== 200) throw new Error(res.data?.message || `Delete failed (${res.status})`);
    } catch (err) {
      // rollback if server failed
      setRows(prev => [...prev, row]);
      console.error(err);
      alert(err.message || "Delete failed.");
    }
  }, []);

  // vendor → items[]
  const vendors = useMemo(() => groupByVendor(rows), [rows]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="todays-orders-modal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="todays-orders-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="todays-orders-modal__card todays-orders-modal__card--stack">
        <button type="button" className="todays-orders-modal__close" aria-label="Close" onClick={onClose}>×</button>

        <div className="todays-orders-modal__content todays-orders-modal__content--stack">
          <div className="todays-orders-modal__title">
            <h2 id="todays-orders-title">Today&apos;s Orders</h2>
          </div>

          <section>
            <p>Each order can be deleted individually.</p>
            <p>⚠️<span style={{ fontWeight: 700, color: "red" }}>Warning: <em>Deleting an order cannot be undone.</em></span></p>

            {vendors.length === 0 ? (
              <p className="todays-orders-modal__empty">No orders yet placed today.</p>
            ) : (
              <ul className="tom-vendor-list">
                {vendors.map(({ vendor, items }) => (
                  <li key={vendor} className="tom-vendor">
                    <header className="tom-vendor__header">
                      <h3 className="tom-vendor__title"><span style={{ fontWeight: 700, color: "Blue" }}>{vendor}</span></h3>
                      <span className="tom-vendor__count">
                        <strong>{items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0)}</strong> total
                      </span>
                    </header>

                    {/* Desktop row layout / Mobile stacked list */}
                    <ul className="tom-order-list">
                      {items.map((o) => (
                        <li
                          key={`${vendor}|${o.supplier}|${o.part}|${o.description}`}
                          className="tom-order"
                        >
                          <div className="tom-order__grid">
                            <div className="cell cell--supplier" data-label="Supplier and Part"><strong>{o.supplier} {o.part}</strong></div>
                            <div className="cell cell--description" data-label="Description">{o.description}</div>
                            <div className="cell cell--qty" data-label="Quantity">Qty: <strong>{Number(o.quantity) || 0}</strong></div>
                            <button
                              className="tom-delete"
                              onClick={() => deleteRowEverywhere({ ...o, vendor })}
                              title="Delete"
                              aria-label={`Delete ${o.supplier} ${o.part}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </li>                            
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}


function OrderHistoryModal({ open, onClose}) {
  // Keep a local, editable copy for optimistic updates
  const [history, setHistory] = useState({});
  const [orderHistory, setOrderHistory] = useState([]);


  useEffect(() => {
    setHistory(orderHistory && typeof orderHistory === "object" ? orderHistory : {});
  }, [orderHistory]);

  // Convert to an array of days sorted DESC (newest first)
  const days = useMemo(() => {
    return Object.entries(history)
      .filter(([_, v]) => v && typeof v === "object")
      .sort((a, b) => b[0].localeCompare(a[0])); // by YYYY-MM-DD
  }, [history]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="todays-orders-modal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-history-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="todays-orders-modal__card todays-orders-modal__card--stack">
        <button type="button" className="todays-orders-modal__close" aria-label="Close" onClick={onClose}>×</button>

        <div className="todays-orders-modal__content todays-orders-modal__content--stack">
          <div className="todays-orders-modal__title">
            <h2 id="order-history-title">Order History</h2>
          </div>

          <section>
            {days.length === 0 ? (
              <p className="todays-orders-modal__empty">No history available.</p>
            ) : (
              <ul className="tom-vendor-list" style={{ gap: 24 }}>
                {days.map(([dateYmd, byVendor]) => {
                  const vendors = Object.keys(byVendor || {}).sort();
                  const dayTotal = vendors.reduce(
                    (sum, v) => sum + (Array.isArray(byVendor[v]) ? byVendor[v].reduce((s, it) => s + (Number(it.quantity) || 0), 0) : 0),
                    0
                  );

                  return (
                    <li key={dateYmd} className="tom-vendor" style={{ border: "1px solid #e5e7eb", borderRadius: 12 }}>
                      <header className="tom-vendor__header" style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <h3 className="tom-vendor__title" style={{ margin: 0 }}>
                          <span style={{ fontWeight: 700 }}>{toMDY(dateYmd)}</span>
                        </h3>
                        <span className="tom-vendor__count"><strong>{dayTotal}</strong> total</span>
                      </header>

                      {/* For each vendor on that date, reuse the same row layout as Today’s */}
                      <ul className="tom-order-list" style={{ paddingTop: 0 }}>
                        {vendors.map((vendor) => {
                          const items = Array.isArray(byVendor[vendor]) ? byVendor[vendor] : [];
                          return (
                            <li key={`${dateYmd}|${vendor}`} className="tom-order" style={{ padding: 0, border: 0, background: "transparent" }}>
                              <div className="tom-vendor__header" style={{ borderTop: "1px solid #e5e7eb", background:"#f8fafc" }}>
                                <h4 className="tom-vendor__title" style={{ margin: 0, fontSize: "1rem", color: "#1e40af" }}>
                                  {vendor}
                                </h4>
                                <span className="tom-vendor__count">
                                  <strong>{items.reduce((s, it) => s + (Number(it.quantity) || 0), 0)}</strong> total
                                </span>
                              </div>

                              <ul className="tom-order-list">
                                {items.map((o) => (
                                  <li
                                    key={`${dateYmd}|${vendor}|${o.supplier}|${o.part}|${o.description}`}
                                    className="tom-order"
                                  >
                                    <div className="tom-order__grid">
                                      <div className="cell cell--supplier" data-label="Supplier and Part">
                                        <strong>{o.supplier} {o.part}</strong>
                                      </div>
                                      <div className="cell cell--description" data-label="Description">{o.description}</div>
                                      <div className="cell cell--qty" data-label="Quantity">Qty: <strong>{Number(o.quantity) || 0}</strong></div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}


// adding comment to rebuild  and change my variables